import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HistoryIcon from "@mui/icons-material/History";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { StoreState } from "../redux/configure-store";
import Engagement from "./Engagement";
import {
  calculateTotalNumberOfHoursForTimesheet,
  isValidTimesheetTimeFormat,
  formatTimesheetDate,
} from "../common/utils";
import {
  fetchEngagements,
  fetchInvoices,
  saveTimesheetDraft,
} from "../user/user-actions";
import InvoicesList from "../common/components/InvoicesList";
import Loading from "../common/components/Loading";
import Snackbar from "../common/components/snackbar";
import { backgrounds } from "../theme/theme";

interface TimesheetsProps {
  engagement: any;
  saveTimesheetDraft: (
    timesheetId: number,
    details: any[],
    notes: string,
    timeFormat: "decimals" | "minutes",
    isSubmitting?: boolean
  ) => Promise<void>;
}

function Timesheets(props: TimesheetsProps) {
  const { engagement, saveTimesheetDraft } = props;

  const [isInitialized, setIsInitialized] = useState(false);
  const [details, setDetails] = useState<any[]>([]);
  const [notes, setNotes] = useState("");
  const [activeTimesheetId, setActiveTimesheetId] = useState<number | null>(null);
  const [timesheetStartDate, setTimesheetStartDate] = useState(new Date().toISOString());
  const [timesheetEndDate, setTimesheetEndDate] = useState(new Date().toISOString());
  const [error, setError] = useState("");
  const [timeFormat, setTimeFormat] = useState<"decimals" | "minutes">("decimals");
  const [totalNumberOfHours, setTotalNumberOfHours] = useState<string | number>(0);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const isTimesheetDaysOffTrackingType = (eng: any) => {
    return eng.trackingType === "daysOff";
  };

  const isTimesheetAvailable = useCallback((): boolean => {
    return (
      engagement &&
      engagement.timesheets &&
      engagement.timesheets.length &&
      !engagement.timesheets[engagement.timesheets.length - 1].submittedAt
    );
  }, [engagement]);

  const initTimesheet = useCallback(() => {
    if (isTimesheetAvailable() && !isInitialized) {
      const activeTimesheet =
        engagement.timesheets[engagement.timesheets.length - 1];
      setIsInitialized(true);
      setActiveTimesheetId(activeTimesheet.id);
      setDetails(activeTimesheet.details);
      setNotes(activeTimesheet.notes);
      setTimesheetStartDate(activeTimesheet.startDate);
      setTimesheetEndDate(activeTimesheet.endDate);
      setTimeFormat(activeTimesheet.timeFormat);
      setTotalNumberOfHours(
        calculateTotalNumberOfHoursForTimesheet(
          activeTimesheet.details,
          activeTimesheet.timeFormat
        )
      );
    }
  }, [engagement, isInitialized, isTimesheetAvailable]);

  useEffect(() => {
    initTimesheet();
  }, [initTimesheet]);

  const changeNotes = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
  };

  const changeNumberOfHours = (date: string, numberOfHours: string) => {
    const newDetails = details.map((detail: any) => {
      if (detail.date !== date) {
        return detail;
      }
      return { date: detail.date, numberOfHours: numberOfHours.trim() };
    });
    setDetails(newDetails);
    setTotalNumberOfHours(
      calculateTotalNumberOfHoursForTimesheet(newDetails, timeFormat)
    );
  };

  const saveDraftOrSubmit = (isSubmitting = false) => {
    let isError = false;
    let errorMessage =
      "Error saving timesheet. All values must be numbers or times.";
    details.forEach((detail: any) => {
      if (detail.numberOfHours === "") {
        isError = true;
        errorMessage =
          "Empty values are not allowed. Please enter an amount for each day.";
      } else if (detail.numberOfHours.indexOf("-") > -1) {
        isError = true;
        errorMessage = "Negative numbers are not allowed";
      } else if (!isValidTimesheetTimeFormat(detail.numberOfHours, timeFormat)) {
        isError = true;
        errorMessage =
          timeFormat === "minutes"
            ? "All values must be in time format (eg. '1', '1.20', '1:35')"
            : "All values must be in decimal format (eg. '1', '1.1', '1.74')";
      }
    });
    if (isError) {
      setError(errorMessage);
      return;
    }

    saveTimesheetDraft(
      activeTimesheetId!,
      details,
      notes,
      timeFormat,
      isSubmitting
    );
  };

  const onChangeTimeFormat = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFormat = e.target.value as "decimals" | "minutes";
    setTimeFormat(newFormat);
    setTotalNumberOfHours(
      calculateTotalNumberOfHoursForTimesheet(details, newFormat)
    );
  };

  const timesheetAvailable = isTimesheetAvailable();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <EditNoteIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" fontWeight={600}>
            Timesheet
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {timesheetAvailable
            ? `${formatTimesheetDate(timesheetStartDate)} - ${formatTimesheetDate(timesheetEndDate)}`
            : "Track your hours for this engagement"}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 3 }}>
        {!timesheetAvailable && (
          <Box
            sx={{
              py: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: "text.secondary",
            }}
          >
            <EventNoteIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography>There are no timesheets available.</Typography>
          </Box>
        )}

        {timesheetAvailable && (
          <>
            {isTimesheetDaysOffTrackingType(engagement) ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Please enter any days you took off during the period:
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatTimesheetDate(timesheetStartDate)} to{" "}
                  {formatTimesheetDate(timesheetEndDate)}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Time Format Selection */}
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                    Time Format
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label="timeFormat"
                    name="timeFormat"
                    value={timeFormat}
                    onChange={onChangeTimeFormat}
                  >
                    <FormControlLabel
                      value="decimals"
                      control={<Radio size="small" />}
                      label="Decimals (1.5 = 1h 30m)"
                    />
                    <FormControlLabel
                      value="minutes"
                      control={<Radio size="small" />}
                      label="Time (1:30 = 1h 30m)"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Time Entries */}
                <Box
                  sx={{
                    backgroundColor: backgrounds.surface,
                    borderRadius: 1,
                    p: 2,
                    mb: 3,
                  }}
                >
                  {details.map((detail: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 1,
                        borderBottom:
                          index < details.length - 1 ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {formatTimesheetDate(detail.date)}
                      </Typography>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={detail.numberOfHours}
                        onChange={(e) =>
                          changeNumberOfHours(detail.date, e.target.value)
                        }
                        inputProps={{
                          style: { textAlign: "center", width: 50 },
                        }}
                        sx={{ width: 80 }}
                      />
                    </Box>
                  ))}

                  {/* Total Row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pt: 2,
                      mt: 1,
                      borderTop: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Total
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        width: 80,
                        textAlign: "center",
                        color: "primary.main",
                      }}
                    >
                      {totalNumberOfHours}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Notes */}
            <TextField
              id="notes"
              name="notes"
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={notes}
              onChange={changeNotes}
              sx={{ mb: 2 }}
            />

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Action Buttons */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={() => saveDraftOrSubmit(false)}
                  sx={{ py: 1.5 }}
                >
                  Save Draft
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => setIsConfirmDialogOpen(true)}
                  sx={{ py: 1.5 }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>

            {/* Confirmation Dialog */}
            <Dialog
              open={isConfirmDialogOpen}
              onClose={() => setIsConfirmDialogOpen(false)}
              aria-labelledby="confirm-submit-dialog-title"
              aria-describedby="confirm-submit-dialog-description"
            >
              <DialogTitle
                id="confirm-submit-dialog-title"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <WarningAmberIcon color="warning" />
                Confirm Submission
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="confirm-submit-dialog-description">
                  Are you sure you want to submit this timesheet? Once submitted,
                  the timesheet cannot be edited.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setIsConfirmDialogOpen(false)}
                  color="inherit"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setIsConfirmDialogOpen(false);
                    saveDraftOrSubmit(true);
                  }}
                  variant="contained"
                  color="primary"
                  autoFocus
                >
                  Submit Timesheet
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Paper>
  );
}

interface RecentTimesheetsProps {
  timesheets: any[];
}

function RecentTimesheets(props: RecentTimesheetsProps) {
  const { timesheets } = props;

  const submittedTimesheets = (timesheets || [])
    .filter((ts: any) => ts.submittedAt)
    .sort(
      (a: any, b: any) =>
        new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    )
    .slice(0, 4);

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = formatTimesheetDate(startDate);
    const end = formatTimesheetDate(endDate);
    return `${start.split(",")[0]} - ${end.split(",")[0]}`;
  };

  const formatSubmittedDate = (date: string) => {
    return formatTimesheetDate(date);
  };

  if (submittedTimesheets.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        mt: 3,
      }}
    >
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <HistoryIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" fontWeight={600}>
            Recently Submitted Timesheets
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Your last {submittedTimesheets.length} submitted timesheet
          {submittedTimesheets.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <Divider />

      <TableContainer sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submittedTimesheets.map((timesheet: any) => (
              <TableRow
                key={timesheet.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: backgrounds.surface },
                }}
              >
                <TableCell>
                  <Typography variant="body2">
                    {formatDateRange(timesheet.startDate, timesheet.endDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {calculateTotalNumberOfHoursForTimesheet(
                      timesheet.details,
                      timesheet.timeFormat
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatSubmittedDate(timesheet.submittedAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label="Submitted"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

interface EngagementsViewProps {
  engagements: any[];
  invoices: any[];
  engagementPublicId: string | number;
  isShowSuccessSnackbar: boolean;
  fetchEngagements: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  saveTimesheetDraft: (
    timesheetId: number,
    details: any[],
    notes: string,
    timeFormat: "decimals" | "minutes",
    isSubmitting?: boolean
  ) => Promise<void>;
}

function EngagementsView(props: EngagementsViewProps) {
  const {
    engagements,
    invoices,
    engagementPublicId,
    isShowSuccessSnackbar,
    fetchEngagements,
    fetchInvoices,
    saveTimesheetDraft,
  } = props;

  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false);

  const handleSuccessSnackbarClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setIsSuccessSnackbarOpen(false);
  };

  useEffect(() => {
    setIsSuccessSnackbarOpen(isShowSuccessSnackbar);
  }, [isShowSuccessSnackbar]);

  useEffect(() => {
    fetchEngagements();
    fetchInvoices();
  }, [fetchEngagements, fetchInvoices]);

  const engagement = engagements.find(
    (e: any) => e.publicId === engagementPublicId
  );
  if (!engagement) {
    return <Loading />;
  }
  const filteredInvoices = invoices.filter((invoice: any) => {
    return invoice.engagement.publicId === engagement.publicId;
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: backgrounds.page,
        py: 4,
        px: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Engagement Details
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your engagement with {engagement.clientName}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Engagement, Recent Timesheets & Invoices */}
          <Grid item xs={12} lg={6}>
            <Engagement engagement={engagement} isListView={false} />
            <RecentTimesheets timesheets={engagement.timesheets} />
            <Box sx={{ mt: 3 }}>
              <InvoicesList invoices={filteredInvoices} />
            </Box>
          </Grid>

          {/* Right Column - Timesheet */}
          <Grid item xs={12} lg={6}>
            <Timesheets
              saveTimesheetDraft={saveTimesheetDraft}
              engagement={engagement}
            />
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        severity="success"
        handleClose={handleSuccessSnackbarClose}
        alertMessage="Timesheet submitted successfully"
        isOpen={isSuccessSnackbarOpen}
      />
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  engagements: storeState.user.engagements,
  invoices: storeState.user.invoices,
  isShowSuccessSnackbar: storeState.user.isShowSuccessSnackbar,
});

export default connect(mapStateToProps, {
  fetchEngagements,
  fetchInvoices,
  saveTimesheetDraft,
})(EngagementsView);
